import { ChangeEvent, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus, X, AlertCircle } from 'lucide-react';
import { compressAndConvertToBase64, dataUrlToFile } from '@/utils/imageProcessor';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface LogoUploadProps {
  value: File | null;
  onChange: (file: File | null) => void;
  initialPreview?: string;
  onProcessed?: (base64Data: string) => void;
}

export function LogoUpload({
  value = null,
  onChange,
  initialPreview,
  onProcessed,
}: LogoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(initialPreview || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setError(null);
    
    if (file) {
      // Check file size - max 5MB before compression
      if (file.size > 5 * 1024 * 1024) {
        setError('File is too large. Please select an image under 5MB.');
        if (inputRef.current) {
          inputRef.current.value = '';
        }
        return;
      }
      
      try {
        setIsProcessing(true);
        // Create a preview immediately for better UX
        const imageUrl = URL.createObjectURL(file);
        setPreview(imageUrl);
        
        // Process the image (compress and convert to base64)
        const processedImageData = await compressAndConvertToBase64(file);
        
        // Convert back to File object with JPEG format
        const processedFile = dataUrlToFile(
          processedImageData,
          `${file.name.split('.')[0]}.jpg`
        );
        
        // Call the onChange with the processed file
        onChange(processedFile);
        
        // Call onProcessed with the base64 data if provided
        if (onProcessed) {
          onProcessed(processedImageData);
        }
        
        // Update preview with processed image
        URL.revokeObjectURL(imageUrl);
        setPreview(processedImageData);
      } catch (error) {
        console.error('Error processing image:', error);
        setError('Failed to process image. Please try a different image.');
        // Don't call onChange on error
      } finally {
        setIsProcessing(false);
      }
    } else {
      onChange(null);
      setPreview(null);
    }
    
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleRemove = () => {
    onChange(null);
    setPreview(null);
    setError(null);
    if (onProcessed) {
      onProcessed('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center">
        {(value || preview) ? (
          <div className="relative h-32 w-32 rounded-lg border bg-gray-50">
            <img
              src={preview || (value ? URL.createObjectURL(value) : '')}
              alt="Store Logo"
              className="h-full w-full object-contain rounded-lg p-2"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 p-1 rounded-full bg-red-500 text-white hover:bg-red-600"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="h-32 w-32 flex flex-col gap-2"
            onClick={() => inputRef.current?.click()}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <div className="animate-pulse">Processing...</div>
            ) : (
              <>
                <ImagePlus className="w-8 h-8" />
                <span className="text-xs">Upload Logo</span>
              </>
            )}
          </Button>
        )}
      </div>
      
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />
      
      <p className="text-[10px] text-muted-foreground">
        Upload your store logo (JPG or PNG format, max 5MB). 
      </p>
    </div>
  );
} 